import { useEffect } from 'react';
/* eslint-disable no-nested-ternary */
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { NOTIFICATION_READ_DELAY, paths } from '@app/constants';
import { getIntervalDurationInDays } from '@app/utils';
import { useMarkNotificationsAsRead } from '@data/hooks';
import { NotificationStatus, ChatEntryDestination } from '@data/sdk/match';
import {
  AntdDrawerProps,
  AntdDrawer,
  Button,
  AntdTypography,
  AntdEmpty,
  AntdDivider,
} from '@ui/components';
import { COLOR_BASE_100, COLOR_BASE_200 } from '@ui/constants';
import { useTheme } from '@ui/hooks';
import { ArrowUpRightIcon } from '@ui/icons';
import { DelayedInView } from './DelayedInView';
import { useNotificationsList } from './hooks/useNotificationsList';

const { Text } = AntdTypography;

interface NotificationsProps extends AntdDrawerProps {
  handleClose: () => void;
}

export const NotificationsPreview = ({ open, handleClose }: NotificationsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { textColor } = useTheme();
  const { pathname } = useLocation();
  const { notifications } = useNotificationsList();
  const { mutate: markAsRead } = useMarkNotificationsAsRead();

  useEffect(() => {
    handleClose();
  }, [pathname]);

  const handleNotificationLink = (trialId?: string, destination?: ChatEntryDestination) => {
    if (trialId) {
      const isUpdatesDestination = destination === ChatEntryDestination.TrialUpdates;

      navigate(
        `/${paths.app}/${paths.trial_recruitment}/${paths.trials}/${trialId}${
          isUpdatesDestination ? `/${paths.updates}` : `/${paths.criterias}`
        }`,
      );
    }
    handleClose();
  };

  return (
    <AntdDrawer
      onClose={handleClose}
      open={open}
      title={t('common.notifications')}
      closeIcon={null}
      size={400}
      extra={
        !!notifications.length && (
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/${paths.app}/${paths.notifications}`)}
            type="text"
            className="text-primary-600 px-0 bg-transparent"
          >
            {t('common.show_all')}
          </Button>
        )
      }
      styles={{
        header: { border: 'none' },
        body: {
          paddingTop: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        },
        wrapper: {
          boxShadow: 'none',
          border: `1px solid ${COLOR_BASE_200}`,
        },
        mask: {
          background: `${COLOR_BASE_100}19`,
        },
      }}
      rootStyle={{
        position: 'fixed',
        top: 0,
      }}
    >
      {notifications.length ? (
        <>
          {notifications.map((notification) => (
            <DelayedInView
              key={notification.createdAt}
              threshold={1}
              delay={NOTIFICATION_READ_DELAY}
              status={notification.status}
              handleReadNotification={() =>
                markAsRead({ start: notification.createdAt, end: notification.createdAt })
              }
            >
              <div className="flex-flex-col w-full">
                <div className="flex justify-between items-center w-full mb-2 gap-1">
                  <div className="flex flex-col gap-0  max-w-[95%]">
                    <Text>{notification.title}</Text>
                    {notification?.context?.id ? (
                      <Button
                        type="text"
                        className="bg-transparent p-0 text-base-700 [&>span]:whitespace-normal h-fit flex text-left text-xs hover:underline"
                        onClick={() =>
                          handleNotificationLink(
                            notification?.context?.id || '',
                            notification?.context?.destination,
                          )
                        }
                      >
                        {notification.text}
                        <div className="flex">
                          <ArrowUpRightIcon className="ml-1" style={{ color: textColor }} />
                        </div>
                      </Button>
                    ) : (
                      <Text className="text-base-700 text-xs">{notification.text}</Text>
                    )}
                    <Text className="text-primary-500 text-xs">
                      {t('common.day_ago', {
                        count: getIntervalDurationInDays(notification.createdAt),
                      })}
                    </Text>
                  </div>
                  {notification.status === NotificationStatus.Unread && (
                    <div className="flex w-2 h-2 rounded-full bg-primary-600" />
                  )}
                </div>
                <AntdDivider className="my-3" />
              </div>
            </DelayedInView>
          ))}
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/${paths.app}/${paths.notifications}`)}
            type="text"
            className="text-primary-600 mt-auto px-0 bg-transparent"
          >
            {t('common.show_all')}
          </Button>
        </>
      ) : (
        <AntdEmpty className="mx-auto" description={t('common.no_notifications_yet')} />
      )}
    </AntdDrawer>
  );
};
